import { ofetch, FetchError } from 'ofetch';
import { ApiClientError } from '../api/errors';
import { createDebugLogger } from '../utils/debug';
import { attachBearer } from '../auth/attachBearer';

const dlog = createDebugLogger('mtff');

/**
 * MTFF Admin-API-Client.
 *
 * Spec:
 *   - vault: 02_applications/07_MTFF/theming.md
 *   - repo:  MTFF/docs/ORBIT-INTEGRATION.md §2.1, §3
 *
 * Geht IMMER über den BFF-Proxy `/proxy/mtff-admin/*`. Der Server-side
 * Token (`MTFF_ADMIN_API_TOKEN`) landet NICHT im Browser-Bundle —
 * server.js injiziert ihn als `Authorization: Bearer <token>`.
 *
 * Per Spec §2.1:
 *   - `X-MTFF-Instance: <id>` für pro-Instance-Scope (Pflicht für
 *     `/instances/:id/...`-Routen außer dem Top-Level `/instances`-LIST)
 *   - `Idempotency-Key: <uuid>` auf Mutations für sichere Retries
 *   - `X-Request-Id` für Trace-Korrelation in mtff_audit_log
 *
 * Mutation-Responses enthalten `{ ok, ...data, auditId }`. Der Caller
 * sollte die `auditId` in seinen eigenen Audit-Log-Eintrag mitschreiben.
 */

const raw = ofetch.create({
  baseURL: '/proxy/mtff-admin',
  onRequest(ctx) { attachBearer(ctx); dlog('→', ctx.request); }, // #501
  onResponse({ request, response }) { dlog('←', request, response.status); },
});

/**
 * Eine UUID v4 für den Idempotency-Key. `crypto.randomUUID()` ist in
 * allen modernen Browsern verfügbar (Chrome 92+, FF 95+, Safari 15.4+).
 */
export function newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback für ältere Browser — nicht-kryptographisch, aber „good enough"
  // für Idempotency (Hauptzweck: derselbe Key bei Retry, nicht globale
  // Unique-ness). Format mirrort UUIDv4.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Hauptaufruf. `instanceId` (kann numeric DB-ID oder URL-encoded
 * "tenant/instance" sein) wird, wenn gesetzt, als `X-MTFF-Instance`-
 * Header durchgereicht. Mutations sollten `mutate: true` setzen damit
 * automatisch ein Idempotency-Key erzeugt wird.
 */
export async function mtffFetch(path, opts = {}) {
  const {
    instanceId,
    mutate = false,
    idempotencyKey,
    headers = {},
    ...rest
  } = opts;
  const finalHeaders = { ...headers };
  if (instanceId != null) finalHeaders['X-MTFF-Instance'] = String(instanceId);
  if (mutate) {
    finalHeaders['Idempotency-Key'] = idempotencyKey ?? newIdempotencyKey();
  }
  try {
    return await raw(path, { ...rest, headers: finalHeaders });
  } catch (err) {
    if (err instanceof FetchError) {
      const data = err.data;
      const code = data?.error?.code ?? 'mtff_request_failed';
      const message = data?.error?.message ?? err.message;
      throw new ApiClientError({
        code,
        message,
        status: err.response?.status ?? 0,
        details: data?.error?.details,
      });
    }
    throw err;
  }
}

export function isMtffProxyEnabled() {
  if (typeof window === 'undefined') return false;
  return window.__ORBIT_CONFIG__?.mtffProxyEnabled === true;
}

export function getMtffPublicUrl() {
  if (typeof window === 'undefined') return '';
  return window.__ORBIT_CONFIG__?.mtffPublicUrl ?? '';
}
